import { singleline } from "@core/foundation/string";
import { mcpApi } from "@core/ports/mcp";
import { z } from "@core/zod";

import { PROJECT_DOMAIN } from "@platform/core";

import { Taxonomy } from "@apis/taxonomy/models";

import { MEDIA_SERVICE } from "../config/index.ts";
import {
  MediaAsset,
  MediaAssetIdSchema,
  MediaAssetRenditionSchema,
} from "../models/index.ts";

import { MediaAssetPreviewSchema } from "./media-internal.ts";

const MAX_GALLERY_IMAGES = 10;

const UploadContentTypeSchema = z.enum([
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "image/webp",
]);

export const mediaMcpApi = mcpApi(
  {
    domain: `mcp.${MEDIA_SERVICE.LABEL}.${PROJECT_DOMAIN}`,
    serverInfo: { name: MEDIA_SERVICE.LABEL, version: "0.1.0" },
  },
  {
    create_media_upload: {
      description: singleline`
        Creates a private media asset for an image you produced yourself and
        returns it with an upload URL. PUT the file's bytes to that URL, then
        call finalize_media_upload.
      `,
      inputSchema: z.object(
        {
          contentType: UploadContentTypeSchema,
          description: z.string(),
          fileName: MediaAsset.shape.fileName,
          height: MediaAsset.shape.height,
          width: MediaAsset.shape.width,
        },
        {
          contentType: singleline`
            The image's MIME type: image/webp, image/jpeg, image/png, image/gif,
            or image/svg+xml
          `,
          description: "What the image shows, kept with the asset",
          fileName: MediaAsset.descriptions.fileName,
          height: MediaAsset.descriptions.height,
          width: MediaAsset.descriptions.width,
        },
      ),
      resultSchema: z.object(
        { mediaAsset: MediaAsset.schema, uploadUrl: z.url() },
        {
          mediaAsset: "The private media asset created for the image",
          uploadUrl: "The URL to PUT the image's bytes to",
        },
      ),
    },
    finalize_media_upload: {
      description: singleline`
        Finalizes a media asset once its bytes are uploaded, so it can be used,
        and starts rendering its preview. Returns the asset.
      `,
      inputSchema: z.object(
        { mediaAssetId: MediaAssetIdSchema },
        { mediaAssetId: "The id of the asset create_media_upload returned" },
      ),
      resultSchema: z.object(
        { mediaAsset: MediaAsset.schema },
        { mediaAsset: "The finalized media asset" },
      ),
    },
    get_media_download_url: {
      description: singleline`
        Returns a URL to download one of your images, with the asset's record:
        its preview, about 1024×1024 pixels in area, or its original. The URL is
        for immediate use, so fetch it right away with a plain GET, and call
        again for a later download. While the preview is still being rendered,
        answers so, and a call a few seconds later returns it.
      `,
      inputSchema: z.object(
        {
          mediaAssetId: MediaAssetIdSchema,
          rendition: MediaAssetRenditionSchema,
        },
        {
          mediaAssetId: "The id of the image's media asset",
          rendition: singleline`
            The file to download: preview for the smaller WebP preview, or
            original for the image itself
          `,
        },
      ),
      resultSchema: z.object(
        { downloadUrl: z.url(), mediaAsset: MediaAsset.schema },
        {
          downloadUrl: "The URL to GET the file from, right away",
          mediaAsset: "The image's media asset",
        },
      ),
    },
    list_media_taxonomies: {
      description: singleline`
        Lists the taxonomies your media is classified with, each with the id the
        Taxonomy MCP's tools take; its get_taxonomy answers a taxonomy's tag
        kinds. The portal's Media page starts the first one.
      `,
      inputSchema: z.object({}),
      resultSchema: z.object(
        { taxonomies: z.array(Taxonomy.schema) },
        { taxonomies: "Your media taxonomies" },
      ),
    },
    preview_media_asset: {
      description: singleline`
        Shows a preview of one of your images, about 1024×1024 pixels in area,
        with the asset's record as its caption. While the preview is still being
        rendered, answers so, and a call a few seconds later shows it.
      `,
      inputSchema: z.object(
        { mediaAssetId: MediaAssetIdSchema },
        { mediaAssetId: "The id of the image's media asset" },
      ),
      kind: "image",
    },
    show_media_gallery: {
      description: singleline`
        Shows up to ten of your images in a gallery widget, in a host rendering
        MCP Apps, and returns their previews' records.
      `,
      inputSchema: z.object(
        {
          mediaAssetIds: z
            .array(MediaAssetIdSchema)
            .nonempty()
            .max(MAX_GALLERY_IMAGES),
        },
        { mediaAssetIds: "The ids of the images' media assets, one to ten" },
      ),
      resultSchema: z.object(
        { previews: z.array(MediaAssetPreviewSchema) },
        { previews: "The previews of the images, in the gallery's order" },
      ),
      view: {
        description: "The gallery widget showing the images' previews",
        name: "media-gallery",
        uri: "ui://media/gallery",
      },
    },
  },
);
